/** Mirrors apps/api/footfall_api/schemas.py. Track lifecycle metadata is
 *  available to operators, but raw appearance vectors and face signatures
 *  never leave the server. */

export type Inward = "up" | "down" | "left" | "right";

export interface EntryLine {
  start: [number, number];
  end: [number, number];
  inward: Inward;
}

/** Processing config of one entrance camera. A branch may have several
 *  entrances, each watched by its own camera with its own entry line
 *  (`Branch.cameras`, keyed by camera id = footage folder = plan marker). */
export interface CameraConfig {
  fps_sample: number;
  detection_confidence: number;
  entry_line: EntryLine;
  /** [width, height] of the frame the entry line was drawn on. The pipeline
   *  resizes this camera's footage to it so the line stays accurate whatever
   *  resolution a given export has; null = processed at native size. */
  frame_size: [number, number] | null;
}

/** One store, grouped governorate → area. Location fields come from the
 *  public store locator (config/branches.json); `video_dir` and `cameras`
 *  are operator-owned. */
export interface Branch {
  id: string;
  name: string;
  governorate: string;
  area: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  plus_code: string | null;
  source_url: string | null;
  timezone: string;
  active: boolean;
  /** Demo/test store — excluded from analytics unless the demo toggle is on. */
  is_demo: boolean;
  /** Footage override of the default `<footage root>/<branch id>`; null = default. */
  video_dir: string | null;
  /** Entrance cameras by camera id; the branch is processable iff non-empty.
   *  Edited via PUT/DELETE `/branches/{id}/cameras/{camera_id}`. */
  cameras: Record<string, CameraConfig>;
  /** Linked footage folders by camera id (any camera): the folder on the
   *  worker PC holding its recordings as `<path>/<YYYYMMDD>/*.mp4`, read in
   *  place instead of copied. Edited via PUT/DELETE
   *  `/branches/{id}/camera-dirs/{camera_id}`; per PC, never synced. */
  camera_dirs: Record<string, string>;
}

/** Whether the pipeline can process this branch. */
export const isConfigured = (branch: Branch): boolean =>
  Object.keys(branch.cameras).length > 0;

/** PATCH body — location fields plus the footage override. Cameras and
 *  linked folders have their own endpoints; `is_demo` is creation-time only. */
export type BranchPatch = Partial<
  Omit<Branch, "id" | "is_demo" | "cameras" | "camera_dirs">
>;

// --- Staff -------------------------------------------------------------------
// Enrolled employees (signed biometric consent on file). Every stored track
// face is matched against them; a match links the track and moves its entry
// into the staff twins of the hourly counts (`staff_entries`, …). Nothing is
// ever removed from the totals — the dashboard's Staff toggle subtracts.

/** One enrollment — roster metadata only. The signature is never returned;
 *  the crop is read separately from a private image endpoint. */
export interface StaffMember {
  id: number;
  branch_id: string;
  name: string;
  note: string;
  face_crop_available: boolean;
  /** Entries this person made across processed days. */
  match_count: number;
  /** Most recent footage day with one, YYYY-MM-DD. */
  last_matched_day: string | null;
  /** Tracks (all cameras, all days) linked to this person, and how many of
   *  them crossed out. */
  track_count: number;
  exit_count: number;
  created_at: string;
  updated_at: string;
}

/** Metadata used when an analyzed track crop is marked as staff. An empty
 *  name is numbered ("Staff 3") by the server. */
export interface StaffCreate {
  name: string;
  note: string;
}

/** Annotation-only update — the face signature itself is immutable. */
export type StaffPatch = Partial<Pick<StaffMember, "name" | "note">>;

/** What an enrollment (or its removal) moved between the counts and the
 *  staff twins. */
export interface StaffApplied {
  tracks: number;
  entries: number;
  exits: number;
  /** Processed days touched, YYYY-MM-DD. */
  days: string[];
}

/** The answer to marking a face: the roster row and what it covered. */
export interface StaffMark {
  staff: StaffMember;
  applied: StaffApplied;
}

// --- Tracks ------------------------------------------------------------------

/** One camera-local ByteTrack identity; raw appearance and face vectors are
 *  intentionally omitted from the response. */
export interface PersonTrack {
  branch_id: string;
  run_date: string;
  camera_id: string;
  tracker_id: number;
  started_at: string;
  ended_at: string;
  duration_seconds: number;
  first_file: string;
  last_file: string;
  first_frame: number;
  last_frame: number;
  observations: number;
  best_confidence: number;
  best_file: string;
  best_frame: number;
  best_bbox: [number, number, number, number];
  entered_at: string | null;
  exited_at: string | null;
  embedding_model: string;
  embedding_dim: number;
  embedding_available: boolean;
  face_confidence: number | null;
  face_crop_width: number | null;
  face_crop_height: number | null;
  face_crop_available: boolean;
  face_embedding_model: string | null;
  face_embedding_dim: number | null;
  face_embedding_available: boolean;
  staff_id: number | null;
  staff_name: string | null;
  staff_similarity: number | null;
  /** What the entry was counted with; null when nothing was estimated. */
  gender: string | null;
  age_bucket: string | null;
  processed_at: string;
}

/** The selected day's headline numbers next to its tracks. */
export interface TrackDayTotals {
  entries: number;
  exits: number;
  staff_entries: number;
  staff_exits: number;
  /** The day's tracks linked to an enrolled staff member. */
  staff_tracks: number;
}

export interface TrackList {
  branch_id: string;
  day: string | null;
  available_days: string[];
  camera_ids: string[];
  total: number;
  limit: number;
  offset: number;
  totals: TrackDayTotals | null;
  tracks: PersonTrack[];
}

// --- Planogram ---------------------------------------------------------------
// Operator-drawn floor plan: vector shapes only, coordinates in metres from
// the outline's top-left corner, y growing downwards.

export type FixtureKind = "shelf" | "counter" | "fridge" | "entrance" | "zone";

/** A run of facings of one product standing on a shelf: `facings` copies of
 *  a `w`×`h` (metres) box side by side, `x` metres from the rack's left end.
 *  `name` is free text — there is no product catalogue behind it. */
export interface Product {
  id: string;
  name: string;
  x: number;
  w: number;
  h: number;
  facings: number;
  /** `#rrggbb` display colour. */
  color: string;
}

/** One shelf board of a rack: the height of its top surface above the floor
 *  (metres) and the products standing on it. */
export interface Shelf {
  id: string;
  elevation: number;
  products: Product[];
}

/** An axis-aligned rectangle on the floor plan. A fixture may also carry its
 *  rack design — `height` above the floor and the `shelves` with their
 *  product facings — drawn as a front elevation in the shelf editor. */
export interface Fixture {
  id: string;
  kind: FixtureKind;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Rack height in metres; null until the rack has been designed. */
  height: number | null;
  shelves: Shelf[];
  /** Whether the fixture cuts camera views (occludes the floor behind it).
   *  null = automatic by kind / rack height — see `fixtureBlocksView`. */
  blocks_view: boolean | null;
}

/** Where a camera is mounted and what it looks at. `rotation` is the aim in
 *  degrees, 0 = towards +x, clockwise; `range` is the drawn cone depth (m). */
export interface CameraMarker {
  id: string;
  label: string;
  x: number;
  y: number;
  rotation: number;
  fov: number;
  range: number;
  /** The entrance camera whose footage the counting pipeline processes. */
  entry: boolean;
}

/** A wall segment (any angle) — the true store boundary and interior
 *  partitions, drawn over the bounding outline. */
export interface Wall {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** The floor plan of one branch, PUT wholesale. */
export interface Planogram {
  width: number;
  height: number;
  fixtures: Fixture[];
  walls: Wall[];
  cameras: CameraMarker[];
}

export interface PlanogramOut extends Planogram {
  branch_id: string;
  updated_at: string;
}

/** A branch's floor plan + entrance-camera setup as one portable document
 *  (GET/PUT /branches/{id}/setup): drawn on one PC, imported on the branch
 *  PC. Configuration only — no footfall data, staff or machine paths. */
export interface BranchSetup {
  format: "footfall-branch-setup";
  version: 1;
  exported_at: string | null;
  branch_id: string;
  branch_name: string;
  cameras: Record<string, CameraConfig>;
  planogram: Planogram | null;
}

/** One camera's occupancy totals over the requested window — aggregates only. */
export interface CameraHeat {
  camera_id: string;
  hours_covered: number;
  samples: number;
  detections: number;
  tracks: number;
  dwell_seconds: number;
  /** detections / samples — average people in view. */
  avg_occupancy: number;
  /** dwell_seconds / tracks — average visible time per visit. */
  avg_dwell_seconds: number;
}

/** The store heatmap for one branch: floor plan + per-camera occupancy. */
export interface BranchHeatmap {
  branch_id: string;
  date_from: string;
  date_to: string;
  hour_from: number | null;
  hour_to: number | null;
  planogram: PlanogramOut | null;
  cameras: CameraHeat[];
}

/** The shared footage directory: `<path>/<branch_id>/<YYYY-MM-DD>/`. */
export interface FootageRoot {
  path: string;
  exists: boolean;
  /** Set from the console (true) or the FOOTFALL_FOOTAGE_ROOT default (false). */
  from_console: boolean;
  /** Camera branches whose directory under the root does not exist yet. */
  branch_dirs_missing: number;
  /** Directories created by the last create call. */
  created: string[];
}

/** One linked camera's day folder (`<folder>/<YYYYMMDD>`) — what a run reads. */
export interface FootageLinked {
  camera_id: string;
  folder: string;
  path: string;
  exists: boolean;
  videos: number;
}

/** One camera of the store and what a run reads for it that day: its linked
 *  folder's day folder, or its subfolder of the day dir. */
export interface FootageCamera {
  camera_id: string;
  label: string;
  entrance: boolean;
  /** The NVR channel it records on, when set. */
  channel: string | null;
  source: "linked" | "copied";
  path: string;
  videos: number;
}

/** Where one branch's recordings for one day go: the copied-footage folder
 *  (one subfolder per camera) plus the cameras linked to their own folder. */
export interface FootageDay {
  branch_id: string;
  day: string;
  path: string;
  exists: boolean;
  videos: number;
  linked: FootageLinked[];
  /** Every camera the store knows, in id order. */
  cameras: FootageCamera[];
}

// --- NVR downloads ----------------------------------------------------------
// The store's recorder (per PC, never synced) and downloading a day from it
// into `<download_folder>/<camera id>/<YYYYMMDD>/`.

/** The store's recorder — never its password. */
export interface NvrInfo {
  branch_id: string;
  configured: boolean;
  host: string;
  username: string;
  has_password: boolean;
  download_folder: string;
  use_https: boolean;
  /** PowerShell runs the download script; without it nothing downloads. */
  powershell: boolean;
  /** NVR channel by camera id. */
  channels: Record<string, string>;
}

/** PUT body; `password` null keeps the stored one. */
export interface NvrInput {
  host: string;
  username: string;
  password: string | null;
  download_folder: string;
  use_https: boolean;
}

export type DownloadStatus = "queued" | "running" | "succeeded" | "failed" | "cancelled";

export interface DownloadCamera {
  camera_id: string;
  channel: string;
  folder: string;
  status: "waiting" | "searching" | "downloading" | "done" | "failed" | "cancelled";
  segments: number | null;
  segments_done: number;
  segments_failed: number;
  bytes_total: number | null;
  bytes_done: number;
  current: string | null;
  note: string | null;
  errors: NvrError[];
  /** The script's exit code (null until it ends) and its own output. */
  exit_code: number | null;
  log: string[];
}

/** One thing that went wrong talking to the NVR: what was asked, and what the
 *  device or the network answered. */
export interface NvrError {
  at: string | null;
  /** "search" or "download"; "script" when the script could not run at all. */
  stage: string | null;
  /** A plain-language guess at the cause, when the answer points at one. */
  hint: string | null;
  message: string;
  url: string | null;
  segment: string | null;
  attempt: number | null;
  type: string | null;
  /** WebExceptionStatus: ConnectFailure, Timeout, … */
  web_status: string | null;
  inner: string | null;
  http_status: number | null;
  http_reason: string | null;
  headers: string | null;
  body: string | null;
}

/** Settings › Database: one table of this PC's database. */
export interface DbTable {
  name: string;
  rows: number;
}

export interface DbColumn {
  name: string;
  type: string;
  /** Holds a secret: masked, and cannot be searched or sorted on. */
  hidden: boolean;
}

export interface DbTablePage {
  table: string;
  columns: DbColumn[];
  rows: (string | number | null)[][];
  total: number;
}

export type LogLevel = "error" | "warning" | "info";

export interface LogFile {
  name: string;
  size: number;
  modified: string;
}

export interface LogFiles {
  folder: string;
  files: LogFile[];
}

export interface LogRead {
  name: string;
  lines: { level: LogLevel; text: string }[];
  /** How many lines passed the filters (`lines` is the last of them). */
  matched: number;
  /** The file is longer than the part that was read. */
  truncated: boolean;
}

export interface HubEvent {
  id: number;
  at: string;
  branch_id: string | null;
  source: string;
  action: string;
  method: string | null;
  url: string | null;
  status: number | null;
  ok: boolean;
  duration_ms: number | null;
  sent_bytes: number | null;
  received_bytes: number | null;
  detail: string | null;
  /** The same failure repeated: how many times, since when (`at` is the last). */
  repeats: number;
  first_at: string | null;
}

export interface HubPending {
  kind: string;
  branch_id: string;
  day: string;
  queued_at: string;
  attempts: number;
  last_error: string | null;
}

export interface HubLog {
  url: string | null;
  registered_at: string | null;
  error: string | null;
  pending: HubPending[];
  events: HubEvent[];
  total: number;
}

/** One Download click: a (branch, day), every camera with a channel. */
export interface Download {
  id: number;
  branch_id: string;
  day: string;
  status: DownloadStatus;
  then_queue: boolean;
  yolo_model: string | null;
  /** The run options it queues with (null when it queues nothing). */
  annotate: boolean | null;
  faces: boolean | null;
  /** "console-download", or "auto-fetch" for the scheduled fetch. */
  requested_by: string;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  /** 0..1 over the cameras (null until the first search answers). */
  progress: number | null;
  cameras: DownloadCamera[];
  message: string | null;
  run_id: number | null;
  /** Processed days ("<branch> <day>") whose footage was deleted to make room. */
  freed_days: string[];
  freed_bytes: number;
  log: string[];
}

/** What the automatic fetch did about one (branch, day). */
export interface AutoFetchEvent {
  at: string;
  branch_id: string;
  day: string;
  outcome: "queued" | "empty" | "failed" | "skipped";
  message: string | null;
  download_id: number | null;
  run_id: number | null;
}

/** The scheduled fetch of the last month's unprocessed days from the NVR. */
export interface AutoFetch {
  /** The desktop app runs the scheduler behind this console. */
  available: boolean;
  enabled: boolean;
  /** Local hours of the day a check starts. */
  hours: number[];
  lookback_days: number;
  max_pending: number;
  annotate: boolean;
  faces: boolean;
  running: boolean;
  activity: string | null;
  current_branch_id: string | null;
  current_day: string | null;
  current_download_id: number | null;
  last_started_at: string | null;
  last_finished_at: string | null;
  last_summary: string | null;
  next_check_at: string | null;
  recent: AutoFetchEvent[];
  /** Days the last check left alone although no run has succeeded for them, and why. */
  excluded: AutoFetchExcluded[];
}

export interface AutoFetchExcluded {
  branch_id: string;
  day: string;
  reason: string;
}

// --- Queue workers ----------------------------------------------------------

export interface WorkerProcess {
  id: string;
  pid: number;
  slot: number | null;
  host: string;
  device: string | null;
  started_at: string;
  seen_at: string;
  run_id: number | null;
  run_branch_id: string | null;
  run_date: string | null;
  run_progress: number | null;
  /** nvidia-smi lists this PID as a CUDA process. */
  on_gpu: boolean;
  gpu_memory_mb: number | null;
}

export interface GpuInfo {
  index: number;
  name: string;
  utilization: number | null;
  memory_used_mb: number | null;
  memory_total_mb: number | null;
  temperature_c: number | null;
  power_w: number | null;
}

export interface GpuProcessInfo {
  pid: number;
  name: string;
  gpu_index: number | null;
  used_memory_mb: number | null;
  is_worker: boolean;
}

/** The queue workers on this PC and the GPU they share (GET /workers). */
export interface WorkersInfo {
  /** The desktop app runs the pool here, so `count` is acted on. */
  supervised: boolean;
  count: number;
  /** Set from the Runs page (true) or the install's default (false). */
  from_console: boolean;
  install_default: number;
  max_count: number;
  workers: WorkerProcess[];
  /** null when this PC has no working NVIDIA driver. */
  gpus: GpuInfo[] | null;
  gpu_processes: GpuProcessInfo[];
}

/** Whether the API host can preprocess native NVR exports (.dav, .264, …):
 *  the ffmpeg the pipeline remuxes them with, and `POST /footage/preview`
 *  converts a camera-setup sample with. */
export interface FfmpegInfo {
  available: boolean;
  path: string | null;
  version: string | null;
  preview_seconds: number;
  preview_max_bytes: number;
}

export interface AreaNode {
  area: string;
  branches: number;
  configured: number;
  /** How many of `branches` are demo stores. */
  demo: number;
}

export interface GovernorateNode {
  governorate: string;
  branches: number;
  configured: number;
  demo: number;
  areas: AreaNode[];
}

/** Place filter shared by analytics views: narrow by governorate, then area,
 *  then optionally specific branches. Empty = everything. */
export interface PlaceFilter {
  governorate: string | null;
  area: string | null;
  branchIds: string[];
}

/** The footfall metrics — everyone counted — and the staff twin of each:
 *  how many of those were enrolled staff. `lib/staff.ts` subtracts the
 *  twins when the Staff toggle says excluded; the API never does. */
export interface MetricTotals {
  entries: number;
  exits: number;
  male: number;
  female: number;
  age_0_17: number;
  age_18_29: number;
  age_30_44: number;
  age_45_plus: number;
  unknown_demo: number;
  staff_entries: number;
  staff_exits: number;
  staff_male: number;
  staff_female: number;
  staff_age_0_17: number;
  staff_age_18_29: number;
  staff_age_30_44: number;
  staff_age_45_plus: number;
  staff_unknown_demo: number;
}

export type Summary = MetricTotals & { hours_covered: number };
export type DailyPoint = MetricTotals & { day: string };
export type HourlyPoint = MetricTotals & { hour_start: string };
export type BranchTotals = MetricTotals & {
  branch_id: string;
  name: string | null;
  governorate: string | null;
  area: string | null;
  /** Hourly rows with data — the branch's own "hours with data". */
  hours_covered: number;
};
/** `DailyPoint` for one branch — the comparison's daily series. */
export type BranchDailyPoint = DailyPoint & { branch_id: string };

/** One (branch, day) of uploaded POS sales, per channel: `store` is a
 *  walk-in sale (what an entry converts into), `delivery` an order nobody
 *  walked in for, `return` a refund. `net` is the day's net after returns. */
export interface SalesDailyPoint {
  branch_id: string;
  day: string;
  store_transactions: number;
  store_net: number;
  delivery_transactions: number;
  delivery_net: number;
  return_transactions: number;
  return_net: number;
  quantity: number;
  discount: number;
  net: number;
}

/** What sales a branch has on file (GET /branches/{id}/sales). */
export interface SalesStatus {
  branch_id: string;
  days: number;
  first_day: string | null;
  last_day: string | null;
  uploaded_at: string | null;
  billing_types: string[];
}

/** What one sales upload did: the days replaced and how each billing type
 *  was classified ("store", "delivery", "return", or "delivery / return"). */
export interface SalesUpload {
  branch_id: string;
  rows: number;
  days: number;
  first_day: string;
  last_day: string;
  channels: Record<string, string>;
  branch_codes: string[];
  status: SalesStatus;
}

export interface HourOfDayPoint {
  hour: number;
  entries: number;
  avg_entries: number;
  staff_entries: number;
  avg_staff_entries: number;
}
/** `HourOfDayPoint` for one branch — the comparison's peak-hours series. */
export type BranchHourOfDayPoint = HourOfDayPoint & { branch_id: string };

export type RunStatus =
  | "queued"
  | "running"
  | "succeeded"
  | "failed"
  | "cancelled";

export interface Run {
  id: number;
  branch_id: string;
  run_date: string;
  status: RunStatus;
  requested_by: string | null;
  queued_at: string;
  started_at: string | null;
  finished_at: string | null;
  hours_written: number | null;
  entries_total: number | null;
  /** Video file names processed by a succeeded run (names only). */
  files_seen: number | null;
  files: string[] | null;
  error: string | null;
  /** Live progress while running: fraction of the day's frames done (null
   *  when the total is unknown) and the file being processed. */
  progress: number | null;
  progress_note: string | null;
  progress_at: string | null;
  /** Detector this run was queued with; null = the worker's default. */
  yolo_model: string | null;
  /** Debug switch: annotated QA videos written. null on older rows. */
  annotate: boolean | null;
  /** Demographics switch: the face pass ran. null on older rows. */
  faces: boolean | null;
  /** What the run actually ran on, recorded once its models loaded. */
  runtime: RunRuntime | null;
}

/** What a run ran on (or, as `WorkerRuntime`, what the next run would). */
export interface RunRuntime {
  device: string | null;
  /** "cuda" | "mps" | "cpu" */
  accelerator: string | null;
  device_label: string | null;
  detector: string | null;
  /** The torch device, or "OpenVINO intel:gpu" for an export. */
  detector_backend: string | null;
  half: boolean | null;
  detect_batch: number | null;
  /** An onnxruntime provider (CUDAExecutionProvider, …), or "off". */
  faces_backend: string | null;
  faces_providers: string[];
  /** "ffmpeg" | "opencv" */
  decoder: string | null;
  /** ffmpeg hwaccel method (cuda = NVDEC, d3d11va, qsv, …) or "none". */
  hwaccel: string | null;
  /** A GPU frame was really decoded with `hwaccel`. */
  hw_decode: boolean | null;
  decoder_line: string | null;
  annotate: boolean | null;
  faces: boolean | null;
}

/** The worker machine's runtime, as its last probe found it. */
export interface WorkerRuntime extends RunRuntime {
  reported_at: string;
  host: string | null;
  ffmpeg: string | null;
  warnings: string[];
  defaults: { annotate: boolean; faces: boolean; face_sample: number } | null;
}

/** GET /runs/runtime. */
export interface WorkerRuntimeStatus {
  report: WorkerRuntime | null;
  /** Set while a re-check is waiting for the worker to be idle. */
  requested_at: string | null;
}

/** One installed person detector a run can pick (GET /runs/models). */
export interface DetectorOption {
  id: string;
  /** Short display name, e.g. "yolo11s · OpenVINO". */
  label: string;
  note: string;
  is_default: boolean;
}

/** Detectors installed on the worker machine; `default` runs when a run
 *  names none. */
export interface DetectorCatalogue {
  default: string;
  models_dir: string | null;
  options: DetectorOption[];
}

/** What POST /runs/reset removed for one branch/day. */
export interface DayReset {
  branch_id: string;
  run_date: string;
  footfall_hours: number;
  camera_hours: number;
  staff_matches: number;
  tracks: number;
  /** Other days this day's runs had left rows on, cleared with it. */
  spill_cleared: string[];
  /** Other days holding its rows too, merged with another run's: left alone. */
  spill_kept: string[];
}

/** What "Send to hub" uploaded: the run's day (its hours) and every other
 *  day of the store the outbox still held, each as the hub now holds it. */
export interface DaySent {
  branch_id: string;
  run_date: string;
  days: string[];
  footfall_hours: number;
  camera_hours: number;
}

export interface DateRange {
  from: string;
  to: string;
}

/** GET /version: what this console is talking to. `version` is null on a
 *  development checkout. On a branch PC, `branch` is the store this PC is
 *  right now (`placeholder` until a hub token or
 *  the operator says which; `local` when it has no hub token, so nothing
 *  about it is synced) and `hub` is the state of its link to the hub; the
 *  console pins itself to the store and says when it is not connected.
 *  Both are null on the hub. */
export interface VersionInfo {
  version: string | null;
  mode: "branch" | "hub";
  hub_url: string | null;
  branch: {
    id: string;
    name: string;
    governorate: string;
    area: string;
    placeholder: boolean;
    local: boolean;
  } | null;
  hub: {
    url: string | null;
    connected: boolean;
    registered_at: string | null;
    error: string | null;
  } | null;
}

/** One of the stores a branch PC knows (Settings › Switch store): a hub
 *  store it holds a token for, or a local one. Never carries the token. */
export interface PcStore {
  id: string;
  name: string;
  governorate: string;
  area: string;
  /** No hub token: its plan and counts stay on this PC. */
  local: boolean;
  active: boolean;
  /** The `this-branch` placeholder: not named yet. */
  placeholder: boolean;
  added_at: string;
}

/** Exactly one of: a hub token, or a name for a local store. */
export type PcStoreCreate = { token: string } | { name: string };

/** A signed-in hub admin. */
export interface User {
  id: number;
  username: string;
}

/** One branch in the hub's fleet view: its token (if any) and what the PC
 *  holding it last reported. */
export interface FleetRow {
  branch_id: string;
  name: string;
  governorate: string;
  area: string;
  timezone: string;
  is_demo: boolean;
  token_prefix: string | null;
  token_created_at: string | null;
  token_created_by: string | null;
  last_seen_at: string | null;
  last_version: string | null;
  last_runtime: string | null;
  last_pending: number | null;
  last_day: string | null;
  last_day_at: string | null;
  days_received: number | null;
  last_setup_at: string | null;
  /** Entrance cameras the branch's setup sync last left here. */
  cameras: number;
  /** Whether that sync also carried a floor plan (the Fleet page opens it). */
  has_plan: boolean;
}

/** A freshly issued branch token — shown once, never retrievable again. */
export interface IssuedToken {
  branch_id: string;
  token: string;
  prefix: string;
  created_at: string;
}

export type ReleaseKind = "setup" | "update";

export interface Release {
  id: number;
  version: string;
  kind: ReleaseKind;
  runtime: string;
  object_key: string;
  sha256: string;
  size_bytes: number;
  notes: string;
  published_at: string;
  is_current: boolean;
}

export interface ReleaseCreate {
  version: string;
  kind: ReleaseKind;
  runtime: string;
  object_key: string;
  sha256: string;
  size_bytes: number;
  notes?: string;
  make_current?: boolean;
}
